import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Keyboard, ListVideo, Loader2, Lock, Maximize2, Minimize2, Ratio, RotateCcw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, libQs, libParams, formatApiError } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { epCode, fmtClock, VideoThumb } from "@/components/VideoBits";
import { EpisodeDetails, TmdbDetails } from "@/components/TmdbDetails";
import { setTheaterActive, toggleTheaterPref, useTheater } from "@/lib/theater";
import { AHEAD_OPTIONS, getVideoPrefs, setVideoPref, useVideoPrefs } from "@/lib/videoPrefs";
import { FIT_MODES, layout, measureBars, modeLabel, nextMode, ratioLabel, resLabel, settleCrop } from "@/lib/videoFit";
import { attachMse, bufferedAhead, mseMime, mseSupported } from "@/lib/msePlayer";
import { Switch } from "@/components/ui/switch";
import { ChapterNav, ChapterPanel, ChapterStrip, SkipButton, chapterIndexAt } from "@/components/VideoChapters";
import { PosterFinder, useCanPoster } from "@/components/PosterFinder";
import { ImagePlus } from "lucide-react";

const KEYS = [["Space / K", "Play · pause"], ["← / →", "−10 s · +10 s"], ["J / L", "−30 s · +30 s"], ["Shift+← / →", "Previous · next chapter"], ["C", "Chapter list"], ["A", "Aspect: Fit · Fill · Stretch · 16:9 · 4:3"], ["↑ / ↓", "Volume"], ["M", "Mute"], ["F", "Full screen"], ["T", "Theater mode"], ["N / P", "Next · previous"]];
const btn = "flex h-9 items-center gap-1 rounded-full border border-zinc-300 bg-white px-3 text-xs hover:border-zinc-900";
const fmtAhead = (s) => (s >= 60 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : `${Math.floor(s)} s`);

/* Options popover: smooth buffering, buffer length, theater. */
const Options = ({ prefs, canSmooth }) => (
  <div className="absolute right-4 top-4 z-10 w-72 space-y-3 rounded-xl bg-black/85 p-4 text-xs text-zinc-200 backdrop-blur" data-testid="video-options">
    <label className="flex items-center justify-between gap-3">
      <span>Auto play<span className="block text-[10px] text-zinc-400">Start the next video when this one ends</span></span>
      <Switch checked={prefs.autoplay !== false} onCheckedChange={(on) => setVideoPref({ autoplay: on })} data-testid="video-autoplay-switch" />
    </label>
    <label className="flex items-center justify-between gap-3">
      <span>Smooth buffering<span className="block text-[10px] text-zinc-400">{canSmooth ? "Keeps extra video ready for slow connections" : "Not available for this file in this browser"}</span></span>
      <Switch checked={!!prefs.smooth} onCheckedChange={(on) => setVideoPref({ smooth: on })} data-testid="video-smooth-switch" />
    </label>
    <label className="flex items-center justify-between gap-3">
      <span>Buffer ahead</span>
      <select value={prefs.ahead} onChange={(e) => setVideoPref({ ahead: Number(e.target.value) })} disabled={!prefs.smooth} data-testid="video-ahead-select" className="rounded-md border border-zinc-600 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 disabled:opacity-50">
        {AHEAD_OPTIONS.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
      </select>
    </label>
    <label className="flex items-center justify-between gap-3">
      <span>Theater mode<span className="block text-[10px] text-zinc-400">Hide the menu and music player while watching</span></span>
      <Switch checked={!!prefs.theater} onCheckedChange={(on) => setVideoPref({ theater: on })} data-testid="video-theater-switch" />
    </label>
    <label className="flex items-center justify-between gap-3">
      <span>Aspect ratio<span className="block text-[10px] text-zinc-400">Default for videos without a remembered choice</span></span>
      <select value={prefs.fit || "fit"} onChange={(e) => setVideoPref({ fit: e.target.value })} data-testid="video-fit-select" className="rounded-md border border-zinc-600 bg-zinc-900 px-2 py-1 text-xs text-zinc-100">
        {FIT_MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </label>
    <label className="flex items-center justify-between gap-3">
      <span>Hide baked-in black bars<span className="block text-[10px] text-zinc-400">Detects letterbox bars encoded in the picture and zooms past them</span></span>
      <Switch checked={prefs.smartCrop !== false} onCheckedChange={(on) => setVideoPref({ smartCrop: on })} data-testid="video-smartcrop-switch" />
    </label>
  </div>
);

/* Full-width HTML5 player: resumes where you left off, saves progress every 10 s, pauses the music player, keyboard shortcuts,
   theater mode, and a Media-Source "smooth buffer" engine with automatic fallback to the plain stream. */
export default function VideoWatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const player = usePlayer();
  const prefs = useVideoPrefs();
  const theater = useTheater();
  const ref = useRef(null);
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const [panel, setPanel] = useState(null); // "help" | "options" | "chapters"
  const [posterOpen, setPosterOpen] = useState(false);
  const canPoster = useCanPoster();
  const [engine, setEngine] = useState("direct"); // "mse" | "direct"
  const [waiting, setWaiting] = useState(false);
  const [ahead, setAhead] = useState(0);
  const [time, setTime] = useState(0);
  const last = useRef({ sent: 0 });
  const resumeAt = useRef(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => { setTheaterActive(true); return () => setTheaterActive(false); }, []);

  // Aspect ratio: natural size from the stream, stage measurements, baked-in bar detection, per-show fit mode.
  const stageRef = useRef(null);
  const [nat, setNat] = useState(0);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [crop, setCrop] = useState(null);
  const samples = useRef([]);
  const groupKey = v?.group?.key;
  const mode = (groupKey && prefs.fitByGroup?.[groupKey]) || prefs.fit || "fit";
  const setMode = useCallback((m) => {
    if (groupKey) setVideoPref({ fitByGroup: { ...(getVideoPrefs().fitByGroup || {}), [groupKey]: m } });
    else setVideoPref({ fit: m });
    toast.message(`Aspect: ${modeLabel(m)}`, { duration: 1200 });
  }, [groupKey]);
  const cycleFit = useCallback(() => setMode(nextMode(mode)), [mode, setMode]);
  useEffect(() => { setNat(0); setCrop(null); samples.current = []; }, [id]);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setStage({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setStage({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [v?.playable]);
  useEffect(() => {
    const el = ref.current;
    if (!el || !v?.playable) return undefined;
    const canvas = document.createElement("canvas");
    let timer = null;
    const onMeta = () => { if (el.videoWidth && el.videoHeight) setNat(el.videoWidth / el.videoHeight); };
    const sample = () => {
      if (el.paused || el.ended) return;
      const m = measureBars(el, canvas);
      if (m) { samples.current.push(m); setCrop(settleCrop(samples.current)); }
      if (samples.current.length === 12) { clearInterval(timer); timer = setInterval(sample, 10000); } // keep checking slowly — bars can only shrink
    };
    const onPlaying = () => { clearInterval(timer); if (prefsRef.current.smartCrop !== false) timer = setInterval(sample, 1500); };
    const onPause = () => clearInterval(timer);
    el.addEventListener("loadedmetadata", onMeta); el.addEventListener("playing", onPlaying); el.addEventListener("pause", onPause);
    onMeta();
    return () => { clearInterval(timer); el.removeEventListener("loadedmetadata", onMeta); el.removeEventListener("playing", onPlaying); el.removeEventListener("pause", onPause); };
  }, [v, engine]);
  const effCrop = prefs.smartCrop === false ? null : crop;
  const natural = nat || v?.aspect || 0;
  const vis = natural ? (effCrop && (mode === "fit" || mode === "fill") ? (natural * (1 - effCrop.l - effCrop.r)) / (1 - effCrop.t - effCrop.b) : natural) : 0;
  const stageHeight = (() => {
    if (theater) return `calc(100vh - ${v?.playable && v.chapters?.length ? 150 : 118}px)`;
    if (!stage.w || !vis) return "clamp(240px, calc(100vh - 330px), 78vh)";
    const maxH = Math.min(Math.max(240, window.innerHeight - 330), window.innerHeight * 0.78);
    const r = mode === "16:9" ? 16 / 9 : mode === "4:3" ? 4 / 3 : mode === "stretch" ? 16 / 9 : vis;
    return `${Math.round(Math.max(240, Math.min(maxH, stage.w / r)))}px`;
  })();
  const lay = layout(stage.w, stage.h, natural, mode, effCrop);
  useEffect(() => {
    setV(null); setErr(""); resumeAt.current = null; last.current = { sent: 0 };
    api.get(`/videos/${id}`).then((r) => setV(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [id]);

  const save = useCallback((finished, el = ref.current) => {
    if (!el || !v || !el.duration || !isFinite(el.duration)) return;
    if (finished) last.current.done = true; else if (last.current.done) return; // never downgrade a finished mark with a later position save
    const position = finished ? el.duration : el.currentTime;
    last.current.sent = position;
    api.put(`/videos/${id}/progress`, { position, duration: el.duration, ...(finished != null ? { finished } : {}) }, { params: libParams() }).catch(() => {});
  }, [id, v]);

  const canSmooth = !!v && mseSupported(v);
  const useMse = canSmooth && prefs.smooth !== false && resumeAt.current === null;

  // Source wiring: smooth-buffer engine (MSE + remux) or the plain progressive stream.
  useEffect(() => {
    const el = ref.current;
    if (!el || !v?.playable) return;
    const start = resumeAt.current ?? (v.progress.finished ? 0 : v.progress.position || 0);
    if (useMse) {
      setEngine("mse");
      const stop = attachMse(el, {
        url: `${API}/videos/${v.id}/remux${libQs()}`, duration: v.duration, mime: mseMime(v), start: start > 3 && start < v.duration - 5 ? start : 0,
        ahead: () => prefs.ahead || 60,
        onLog: (m) => console.debug("[smooth-buffer]", m),
        onFallback: (pos, why) => { resumeAt.current = pos; setErr(""); setEngine("direct"); toast.message("Switched to the standard player", { description: why }); setV((x) => ({ ...x })); },
      });
      el.play?.()?.catch(() => {});
      return stop;
    }
    setEngine("direct");
    el.src = `${API}/videos/${v.id}/stream${libQs()}`;
    const onMeta = () => { if (start > 3 && start < el.duration - 5) el.currentTime = start; el.play?.()?.catch(() => {}); };
    el.addEventListener("loadedmetadata", onMeta);
    return () => { el.removeEventListener("loadedmetadata", onMeta); el.removeAttribute("src"); el.load(); };
  }, [v, useMse]); // eslint-disable-line react-hooks/exhaustive-deps

  // Progress, buffering indicator, auto-next, errors.
  useEffect(() => {
    const el = ref.current;
    if (!el || !v?.playable) return;
    player.pause?.();
    let timer = null, stall = null;
    const onTime = () => { setAhead(bufferedAhead(el)); setTime(el.currentTime || 0); if (Math.abs(el.currentTime - last.current.sent) >= 10) save(); };
    const onWait = () => {
      setWaiting(true);
      if (el.src.startsWith("blob:") && !stall) stall = setTimeout(() => { // smooth-buffer stream starved for 10 s → plain stream from the same spot
        stall = null;
        if (el.readyState >= 3 || !el.src.startsWith("blob:")) return;
        resumeAt.current = el.currentTime || 0; toast.message("Switched to the standard player", { description: "The smooth buffer stalled" }); setV((x) => ({ ...x }));
      }, 10000);
    };
    const onPlaying = () => { setWaiting(false); if (stall) { clearTimeout(stall); stall = null; } };
    const onEnded = () => {
      save(true);
      if (v.next_id && prefsRef.current.autoplay !== false) timer = setTimeout(() => nav(`/videos/watch/${v.next_id}`), 800);
    };
    const onError = async () => {
      if (!el.getAttribute("src")) return;
      if (el.src.startsWith("blob:")) { // decode failure inside the smooth-buffer engine → plain stream from the same spot
        resumeAt.current = el.currentTime || 0; setErr(""); toast.message("Switched to the standard player", { description: "The smooth-buffer stream could not be decoded" }); setV((x) => ({ ...x })); return;
      }
      try {
        const r = await fetch(el.currentSrc || el.src, { headers: { Range: "bytes=0-0" }, credentials: "include" });
        if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.detail || `The video could not be fetched (HTTP ${r.status})`); return; }
      } catch { setErr("The video could not be fetched — check your connection"); return; }
      setErr(`This ${v.ext.toUpperCase()} file couldn't be decoded by your browser (unsupported video or audio codec${v.vcodec ? `: ${v.vcodec}` : ""}).`);
    };
    const onPause = () => save();
    const onHide = () => { if (document.visibilityState === "hidden") save(); };
    const pairs = [["timeupdate", onTime], ["progress", onTime], ["waiting", onWait], ["playing", onPlaying], ["canplay", onPlaying], ["ended", onEnded], ["error", onError], ["pause", onPause]];
    pairs.forEach(([e, f]) => el.addEventListener(e, f));
    document.addEventListener("visibilitychange", onHide);
    return () => {
      pairs.forEach(([e, f]) => el.removeEventListener(e, f));
      document.removeEventListener("visibilitychange", onHide);
      if (timer) clearTimeout(timer);
      if (stall) clearTimeout(stall);
      save(undefined, el);
    };
  }, [v, save, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest("input, textarea, select, [contenteditable]")) return;
      const k = e.key.toLowerCase();
      if (k === "n" && v?.next_id) return nav(`/videos/watch/${v.next_id}`);
      if (k === "p" && v?.prev_id) return nav(`/videos/watch/${v.prev_id}`);
      if (k === "?") return setPanel((p) => (p === "help" ? null : "help"));
      if (k === "t") return toggleTheaterPref();
      if (k === "a") return cycleFit();
      if (k === "c" && v?.chapters?.length) return setPanel((p) => (p === "chapters" ? null : "chapters"));
      const el = ref.current;
      if (!el) return;
      const seek = (d) => { el.currentTime = Math.max(0, Math.min(el.duration || 0, el.currentTime + d)); };
      if (e.shiftKey && (k === "arrowleft" || k === "arrowright") && v?.chapters?.length) {
        e.preventDefault();
        const ch = v.chapters, cur = chapterIndexAt(ch, el.currentTime);
        if (k === "arrowright") { if (cur < ch.length - 1) el.currentTime = ch[cur + 1].start; }
        else el.currentTime = cur > 0 && el.currentTime - ch[cur].start < 3 ? ch[cur - 1].start : ch[Math.max(cur, 0)].start;
        return;
      }
      if (k === " " || k === "k") { e.preventDefault(); el.paused ? el.play()?.catch(() => {}) : el.pause(); }
      else if (k === "arrowleft") seek(-10); else if (k === "arrowright") seek(10);
      else if (k === "j") seek(-30); else if (k === "l") seek(30);
      else if (k === "arrowup") { e.preventDefault(); el.volume = Math.min(1, el.volume + 0.1); } else if (k === "arrowdown") { e.preventDefault(); el.volume = Math.max(0, el.volume - 0.1); }
      else if (k === "m") el.muted = !el.muted;
      else if (k === "f") { document.fullscreenElement ? document.exitFullscreen() : el.parentElement.requestFullscreen?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [v, nav, cycleFit]);

  const markWatched = async () => {
    try {
      const dur = v.duration || ref.current?.duration || 0;
      await api.put(`/videos/${id}/progress`, { position: dur, duration: dur, finished: true }, { params: libParams() });
      toast.success("Marked as watched"); last.current.done = true; setV({ ...v, progress: { ...v.progress, finished: true, pct: 100 } });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const startOver = async () => {
    try {
      await api.delete(`/videos/${id}/progress`, { params: libParams() });
      last.current = { sent: 0 };
      if (ref.current) { ref.current.currentTime = 0; ref.current.play()?.catch(() => {}); }
      setV({ ...v, progress: { position: 0, pct: 0, finished: false } });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err && !v) return <p className="text-sm text-red-600" data-testid="video-error">{err}</p>;
  if (!v) return <Loader2 className="animate-spin text-zinc-400" />;
  const chapters = v.chapters || [];
  const seekTo = (t) => { const el = ref.current; if (!el) return; el.currentTime = t; setTime(t); el.play?.()?.catch(() => {}); };
  const refreshChapters = async () => {
    try {
      const r = await api.post(`/videos/${id}/chapters/refresh`);
      setV((x) => ({ ...x, chapters: r.data.chapters, chapters_source: r.data.chapters_source }));
      toast.success(r.data.embedded ? `Found ${r.data.embedded} chapter marks` : "No chapter marks in this file");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const pad = theater ? "px-4 sm:px-6" : "px-4 sm:px-8";
  return (
    <div data-testid="video-watch-page" data-theater={theater ? 1 : 0} data-engine={engine} className={theater ? "" : "-mx-4 sm:-mx-8"}>
      <div className={`flex items-center justify-between py-2 ${pad}`}>
        <Link to={`/videos/group/${encodeURIComponent(v.group.key)}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="video-watch-back"><ArrowLeft size={12} />{v.group.name}</Link>
        <div className="flex items-center gap-2">
          {v.playable && engine === "mse" && <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700 sm:inline-flex" title="Smooth-buffer player active" data-testid="video-buffer-chip">Buffered {fmtAhead(ahead)} ahead</span>}
          <button onClick={toggleTheaterPref} className={btn + " !h-8"} title="Theater mode (T)" data-testid="video-theater-button">{theater ? <Minimize2 size={13} /> : <Maximize2 size={13} />}{theater ? "Exit theater" : "Theater"}</button>
          {v.playable && (
            <button onClick={cycleFit} className={btn + " !h-8"} title={`Aspect ratio (A) — ${FIT_MODES.find(([k]) => k === mode)?.[2] || ""}${groupKey ? `. Remembered for ${v.group.name}` : ""}`} data-testid="video-fit-button">
              <Ratio size={13} />{modeLabel(mode)}{vis ? <span className="font-mono text-zinc-400" data-testid="video-ratio-badge">{ratioLabel(vis)}</span> : null}{effCrop && (mode === "fit" || mode === "fill") ? <span className="rounded bg-amber-400/20 px-1 text-[10px] text-amber-700" title="Baked-in black bars detected and hidden" data-testid="video-crop-badge">bars hidden</span> : null}
            </button>
          )}
          {v.playable && <button onClick={() => setPanel((p) => (p === "chapters" ? null : "chapters"))} className={btn + " !h-8"} title="Chapters (C)" data-testid="video-chapters-button"><ListVideo size={13} />Chapters{chapters.length ? <span className="text-zinc-400">{chapters.length}</span> : null}</button>}
          <button onClick={() => setPanel((p) => (p === "options" ? null : "options"))} className={btn + " !h-8"} data-testid="video-options-button"><Settings2 size={13} />Options</button>
          <button onClick={() => setPanel((p) => (p === "help" ? null : "help"))} className={btn + " !h-8"} data-testid="video-watch-help"><Keyboard size={13} />Shortcuts</button>
        </div>
      </div>
      <div ref={stageRef} className="relative overflow-hidden bg-black" data-testid="video-stage" data-fit={mode} data-ratio={vis ? ratioLabel(vis) : undefined} data-crop={effCrop ? 1 : 0} style={{ height: stageHeight }}>
        {v.playable ? (
          <video ref={ref} key={v.id} controls playsInline preload="auto" data-testid="video-element"
            className={lay ? "absolute" : "mx-auto h-full w-full"}
            style={lay ? { left: lay.box.x, top: lay.box.y, width: lay.box.w, height: lay.box.h, objectFit: lay.fit, objectPosition: lay.pos, maxWidth: "none", transition: "width 200ms, height 200ms, left 200ms, top 200ms" } : undefined} />
        ) : (
          <div className="mx-auto flex h-full w-full flex-col items-center justify-center gap-3 text-center text-zinc-300" data-testid="video-unplayable-stage">
            <Lock size={28} />
            <p className="text-sm font-medium">{v.ext.toUpperCase()} files can't be played in a browser</p>
            <p className="max-w-md text-xs text-zinc-500">Browsers only decode MP4, M4V, MOV and WebM. Convert this file (e.g. with HandBrake to MP4/H.264) and put it back in the folder, then scan again.</p>
          </div>
        )}
        {waiting && v.playable && !err && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid="video-buffering"><span className="flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs text-white"><Loader2 size={14} className="animate-spin" />Buffering…</span></div>}
        {err && v.playable && <p className="absolute inset-x-0 bottom-16 mx-auto w-fit rounded-full bg-red-600/90 px-4 py-1.5 text-xs text-white" data-testid="video-decode-error">{err}</p>}
        {panel === "help" && (
          <div className="absolute right-4 top-4 z-10 rounded-xl bg-black/80 p-4 text-xs text-zinc-200 backdrop-blur" data-testid="video-shortcuts">
            {KEYS.map(([k, d]) => <p key={k} className="flex justify-between gap-6 py-0.5"><kbd className="font-mono text-amber-300">{k}</kbd><span>{d}</span></p>)}
          </div>
        )}
        {panel === "options" && <Options prefs={prefs} canSmooth={canSmooth} />}
        {panel === "chapters" && <ChapterPanel chapters={chapters} source={v.chapters_source} time={time} onSeek={seekTo} onRefresh={refreshChapters} canRefresh={!libQs()} onClose={() => setPanel(null)} />}
        {v.playable && <SkipButton chapters={chapters} source={v.chapters_source} time={time} onSeek={seekTo} onNext={v.next_id ? () => { save(true); nav(`/videos/watch/${v.next_id}`); } : null} />}
      </div>
      {v.playable && chapters.length > 0 && <div className={`mt-2 ${pad}`}><ChapterStrip chapters={chapters} time={time} duration={v.duration} onSeek={seekTo} /></div>}
      <div className={`mt-3 flex flex-wrap items-center justify-center gap-2 ${pad}`} data-testid="video-actions">
        {v.playable && chapters.length > 0 && <ChapterNav chapters={chapters} time={time} onSeek={seekTo} btn={btn} only="prev" />}
        {v.next_id && (
          <label className="flex h-9 items-center gap-2 rounded-full border border-zinc-300 bg-white px-3 text-xs" title="Start the next video automatically when this one ends" data-testid="video-autoplay-toggle">
            Auto play<Switch checked={prefs.autoplay !== false} onCheckedChange={(on) => setVideoPref({ autoplay: on })} className="scale-75" />
          </label>
        )}
        {v.playable && chapters.length > 0 && <ChapterNav chapters={chapters} time={time} onSeek={seekTo} btn={btn} only="next" />}
        {v.playable && (v.progress.finished || v.progress.pct > 0) && <button onClick={startOver} data-testid="video-start-over" className={btn}><RotateCcw size={13} />Start over</button>}
        {!v.progress.finished && <button onClick={markWatched} data-testid="video-mark-watched" className={btn}><Check size={13} />Mark watched</button>}
        {v.prev_id && <Link to={`/videos/watch/${v.prev_id}`} data-testid="video-prev" className={btn}><ChevronLeft size={13} />Previous</Link>}
        {v.next_id && <Link to={`/videos/watch/${v.next_id}`} data-testid="video-next" className={btn}>Next<ChevronRight size={13} /></Link>}
      </div>
      <div className={`mt-4 flex gap-5 sm:gap-7 ${pad}`} data-testid="video-info">
        <div className="w-28 shrink-0 sm:w-40" data-testid="video-cover">
          <VideoThumb id={v.id} hasThumb={v.has_thumb} thumbV={v.thumb_v} poster={v.poster || v.group.poster} portrait={!!(v.poster || v.group.poster)} className="shadow-lg" />
          {canPoster && v.group.kind !== "show" && (
            <button onClick={() => setPosterOpen(true)} data-testid="video-change-poster" className={btn + " mt-2 w-full justify-center"}><ImagePlus size={13} />{v.poster ? "Change poster" : "Add poster"}</button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-xl font-semibold leading-tight text-zinc-900 sm:text-2xl" data-testid="video-title">{v.group.kind === "show" && epCode(v) ? <span className="mr-2 font-mono text-sm font-normal text-zinc-400">{epCode(v)}</span> : null}{v.title}</h1>
          <p className="mt-1 text-xs text-zinc-500" data-testid="video-meta">{[v.group.kind === "show" ? (v.group.tmdb?.title || v.group.name) : null, v.file_title && v.file_title !== v.title ? `file: ${v.file_title}` : null, v.tmdb?.year || v.year, v.duration ? fmtClock(v.duration) : null, v.ext.toUpperCase(), v.width && v.height ? `${resLabel(v.width, v.height)} ${v.width}×${v.height}` : null, v.vcodec ? v.vcodec.toUpperCase() : null, v.progress.finished ? "Watched" : v.progress.pct > 0 ? `${Math.round(v.progress.pct)}% watched` : null].filter(Boolean).join(" · ")}</p>
          {(v.tmdb || v.episode_info || v.group.tmdb) && (
            <div className="mt-5 space-y-4" data-testid="video-details">
              {v.tmdb && <TmdbDetails d={v.tmdb} testid="movie-details" />}
              {v.episode_info && <EpisodeDetails e={v.episode_info} />}
              {v.group.tmdb && (
                <div className="border-t border-zinc-200 pt-4">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">About the show</p>
                  <TmdbDetails d={v.group.tmdb} compact testid="show-details" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {canPoster && v.group.kind !== "show" && (
        <PosterFinder target={{ kind: "video", key: v.id }} title={v.title} hasPoster={!!v.poster} open={posterOpen} onOpenChange={setPosterOpen}
          onApplied={() => setTimeout(() => api.get(`/videos/${id}`).then((r) => setV((x) => ({ ...x, poster: r.data.poster, tmdb: r.data.tmdb }))), 1500)} />
      )}
    </div>
  );
}
